import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import ReactDOM from "react-dom";
import { cn } from "@/shared/lib/utils";
import styles from "./ShareSelect.module.scss";
/**
 * Позиционирование меню под/над контролом с ограничением высоты по вьюпорту —
 * иначе длинный список уходил за нижний край и обрезался (скролл не включался).
 */
function menuStyle(rect) {
    const GAP = 6;
    const MARGIN = 12;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const spaceBelow = vh - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    const flipUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(140, Math.floor(flipUp ? spaceAbove : spaceBelow));
    return {
        position: "fixed",
        left: rect.left,
        width: rect.width,
        maxHeight,
        ...(flipUp
            ? { bottom: vh - rect.top + GAP }
            : { top: rect.bottom + GAP }),
    };
}
/**
 * Кастомный дропдаун (вместо нативного <select>, чей список опций не стилизуется
 * кроссбраузерно). Список рендерится порталом с fixed-позиционированием — иначе
 * его обрезал бы скролл-контейнер редактора. Закрывается по клику вне, Esc,
 * скроллу снаружи меню и ресайзу.
 */
export const ShareSelect = ({ label, value, onChange, options, placeholder = "— не выбрано —", className, }) => {
    const [open, setOpen] = React.useState(false);
    const [rect, setRect] = React.useState(null);
    const btnRef = React.useRef(null);
    const listRef = React.useRef(null);
    const selected = options.find((o) => o.value === value);
    const place = React.useCallback(() => {
        if (btnRef.current)
            setRect(btnRef.current.getBoundingClientRect());
    }, []);
    const toggle = () => {
        if (!open)
            place();
        setOpen((v) => !v);
    };
    React.useEffect(() => {
        if (!open)
            return;
        const openedAt = Date.now();
        const onDoc = (e) => {
            const t = e.target;
            if (btnRef.current?.contains(t) || listRef.current?.contains(t))
                return;
            setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape")
                setOpen(false);
        };
        // Скролл внутри меню — ок. Скролл страницы/редактора — закрываем,
        // иначе fixed-меню «отлипает» от контрола и наезжает на другие блоки.
        // Сразу после открытия возможен scroll-into-view — только перепозиционируем.
        const onScroll = (e) => {
            const t = e.target;
            if (t instanceof Node && listRef.current?.contains(t))
                return;
            if (Date.now() - openedAt < 150) {
                place();
                return;
            }
            setOpen(false);
        };
        const onResize = () => setOpen(false);
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
        };
    }, [open, place]);
    return (_jsxs("div", { className: cn(styles.wrap, className), children: [label && _jsx("span", { className: styles.label, children: label }), _jsxs("button", { ref: btnRef, type: "button", className: styles.control, onClick: toggle, "aria-haspopup": "listbox", "aria-expanded": open, children: [_jsx("span", { className: cn(styles.value, !selected && styles.placeholder), children: selected ? selected.label : placeholder }), _jsx("svg", { className: cn(styles.caret, open && styles.caretOpen), width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M2 4.5L6 8.5L10 4.5" }) })] }), open &&
                rect &&
                typeof document !== "undefined" &&
                ReactDOM.createPortal(_jsx("div", { ref: listRef, className: styles.menu, role: "listbox", style: menuStyle(rect), children: options.map((o) => (_jsx("button", { type: "button", role: "option", "aria-selected": o.value === value, className: cn(styles.option, o.value === value && styles.optionSelected), onClick: () => {
                            onChange(o.value);
                            setOpen(false);
                        }, children: o.label }, o.value))) }), document.body)] }));
};
